import { Injectable } from '@angular/core'
import { QuoteSession } from '@app/models/quote-session.model'
import { QuoteSessionService } from '@app/services/quote-session.service'
import { State, Action, StateContext, Store, Selector, createSelector } from '@ngxs/store'
import { firstValueFrom, tap } from 'rxjs'
import { QuoteAction } from '@app/shared/store/quote/quote.actions'
import { QuoteSessionAction } from './quote-session.actions'
import { ContactAction } from '@app/shared/store/contact/contact.actions'
import { PetAction } from '@app/shared/store/pet/pet.actions'
import { ReferralAction } from '@app/shared/store/referral/referral.actions'
import { PetState, PetStateModel } from '@app/shared/store/pet/pet.state'
import { Pet } from '@app/models/pet.model'
import { UserState, UserStateModel } from '@app/shared/store/user/user.state'
import { patch } from '@ngxs/store/operators'
import { Contact } from '@app/models/contact'
import { QuoteService } from '@app/services/quote.service'
import { Quote } from '@app/models/quote.model'
import { AppState, AppStateModel } from '@app/shared/store/app.state'
import { AppActions } from '../app.actions'
import { BusinessRulesService } from '@app/services/business-rules.service'
import { Species } from '@app/models/species'
import { Address } from '@app/models/address'
import { BillingInformation } from '@app/models/billing-information'
import { ResetForm } from '@ngxs/form-plugin'
import { PaymentType } from '@app/models/payment-type'
import { PurchaseResponse } from '@app/models/purchase-response.model'
import { QuoteFlowAction } from '../quote-flow/quote-flow.actions'
import { SalesProcessToolAction } from '../sales-process-tool/sales-process-tool.actions'
import { Nullable } from '@app/models/nullable'
import { BrandState, BrandStateModel } from '../brand/brand.state'
import { StripeService } from '@app/services/stripe.service'

export interface QuoteSessionStateModel extends QuoteSession {}

const defaults: QuoteSessionStateModel = {
	quote: null,
	pets: [], // move to it's own state slice
	contact: null,
	saveEntityToCrm: false,
	loadingIndicators: [],
	loadingOverrides: [],
	billingSchedules: [],
	crmUserId: null,
	quoteStarted: false,
	tabProgress: 0,
	paymentSessions: [],
}

@State<QuoteSessionStateModel>({
	name: 'quoteSession',
	defaults,
})
@Injectable()
export class QuoteSessionState {
	constructor(
		private readonly quoteSessionService: QuoteSessionService,
		private readonly quoteService: QuoteService,
		private readonly businessRulesService: BusinessRulesService,
		private readonly stripeService: StripeService,
		private readonly store: Store
	) {}

	@Action(QuoteSessionAction.CreateNew)
	async createNewSession({ getState, setState, dispatch }: StateContext<QuoteSessionStateModel>) {
		const state = getState()
		const crmUserId = this.store.selectSnapshot<UserStateModel>(UserState).crmUserId
		const allowNewSession = this.store.selectSnapshot<AppStateModel>(AppState).allowNewSession
		try {
			if (!!state.id && !allowNewSession) return
			const session = {
				...(await firstValueFrom(this.quoteSessionService.createNewEmptySession())),
				crmUserId,
			}
			await firstValueFrom(
				dispatch(new ResetForm({ path: 'acknowledgement.acknowledgementsForm' }))
			)
			setState(session)
			await firstValueFrom(dispatch(new PetAction.Clear()))
			await firstValueFrom(dispatch(new AppActions.DisallowSessionCreation()))
			await firstValueFrom(dispatch(new QuoteSessionAction.Quote.ResetStartDate()))
			await firstValueFrom(dispatch(new QuoteFlowAction.Reset()))
			await firstValueFrom(dispatch(new SalesProcessToolAction.ResetState()))

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.New)
	async newSession({ dispatch }: StateContext<QuoteSessionState>) {
		const policyStartDate = new Date()
		const policyRenewalDate = new Date()
		policyStartDate.setDate(policyStartDate.getDate() + 1)
		policyRenewalDate.setDate(policyStartDate.getDate() + 365)
		const quoteSession: QuoteSession = {
			quote: {
				policyStartDate,
				policyRenewalDate,
				paymentModality: 'Monthly',
			},
			contact: {},
			pets: [],
			saveEntityToCrm: false,
			loadingIndicators: [],
			loadingOverrides: [],
			billingSchedules: [],
			errors: [],
		}
		try {
			await firstValueFrom(dispatch(new QuoteSessionAction.Update(quoteSession)))
			await firstValueFrom(dispatch(new ReferralAction.ClearSearchZip()))

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Load)
	load(
		{ setState, dispatch }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Load
	) {
		// save the state of the quote session
		setState(payload)
		dispatch([
			// dispatch loadQuote for quoteState
			new QuoteAction.Load(payload.quote!),
			// dispatch loadContact for contactState
			new ContactAction.Load(payload.contact!),
			// dispatch loadPets for loadPets
			new PetAction.Load(payload.pets!),
		])
	}

	@Action(QuoteSessionAction.LoadTabProgress)
	loadTabProgress(
		{ patchState }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.LoadTabProgress
	) {
		patchState({ tabProgress: payload })
	}

	@Action(QuoteSessionAction.Update)
	update(
		{ setState }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Update
	) {
		setState(
			patch<QuoteSessionStateModel>({
				...payload,
				quote: patch<Quote>({ ...payload.quote }),
				contact: patch<Contact>({ ...payload.contact }),
			})
		)
	}

	@Action(QuoteSessionAction.Save)
	save({ setState }: StateContext<QuoteSessionStateModel>, { payload }: QuoteSessionAction.Save) {
		setState({ ...payload })
	}

	@Action(QuoteSessionAction.SaveSession)
	async saveSession({ getState, dispatch }: StateContext<QuoteSessionStateModel>) {
		const state = getState()
		const petState = this.store.selectSnapshot<Pet[]>((state) => state.pet.pets)
		try {
			const res = await firstValueFrom(
				this.quoteSessionService.saveSession({ ...state, pets: petState })
			)
			await firstValueFrom(dispatch(new QuoteSessionAction.Update(res)))
			await firstValueFrom(dispatch(new QuoteSessionAction.LoadTabProgress(res.tabProgress!)))
			if (!!res.pets.length) {
				await firstValueFrom(dispatch(new PetAction.Load(res.pets)))
			}

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Upsert)
	async upsertSession({ getState, dispatch }: StateContext<QuoteSessionStateModel>) {
		const state = getState()
		const petState = this.store.selectSnapshot<Pet[]>((state) => state.pet.pets)
		try {
			const res = await firstValueFrom(
				this.quoteSessionService.upsertQuoteSession({ ...state, pets: petState })
			)
			await firstValueFrom(dispatch(new QuoteSessionAction.Update(res)))
			await firstValueFrom(dispatch(new QuoteSessionAction.LoadTabProgress(res.tabProgress!)))
			if (!!res.pets.length) {
				await firstValueFrom(dispatch(new PetAction.Load(res.pets)))
			}

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.SaveForClose)
	async saveForClose({ getState }: StateContext<QuoteSessionStateModel>) {
		const state = getState()
		const petState = this.store.selectSnapshot<Pet[]>((state) => state.pet.pets)
		/**
		 * When saving for later, if the properties for the pet are sent with
		 * null values it causes the call to fail. In the old version these were
		 * just undefined, but in this version they are initiated with null values
		 * when the form is created. This causes issues with parsing the `ageInYears`
		 * and other values when sent to the backend. This value sends a
		 * `cleanedPet` where it strips values that are not able to be parsed by the backend
		 */
		const cleanedPetState = petState.map((entry) => {
			const cleanedPet = Object.fromEntries(
				Object.entries(entry).filter(([_key, value]) => !!value)
			) as Pet

			return cleanedPet
		})
		try {
			await firstValueFrom(
				this.quoteSessionService.saveForClose({ ...state, pets: cleanedPetState })
			)

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.ActivatePolicyQuote)
	async activatePolicyQuote({
		getState,
		setState,
		dispatch,
	}: StateContext<QuoteSessionStateModel>) {
		try {
			const session = getState()
			const petList = await firstValueFrom(this.store.select<Pet[]>((s) => s.pet.pets))
			const pets = petList
				.map((pet) => ({
					...pet,
					species: pet.species === Species.Dog ? 'Dog' : 'Cat',
				}))
				.filter((p) => Object.entries(p).length >= 10)
			const res: QuoteSession = await firstValueFrom(
				// @ts-expect-error type issues with species
				this.businessRulesService.activatePolicyQuote({ ...session, pets })
			)
			setState(res)
			// pipe pets back in to PetState from response
			if (res.pets.length) {
				await firstValueFrom(dispatch(new PetAction.Load(res.pets)))
			}

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Pets.ClearCoverages)
	async clearPetCoverages({ getState, setState, dispatch }: StateContext<QuoteSessionStateModel>) {
		try {
			const session = getState()
			const petState = await firstValueFrom(this.store.select<PetStateModel>(PetState))
			const pets: Pet[] = petState.pets.map((p) => ({ ...p, petCoverage: undefined }))
			const res: QuoteSession = await firstValueFrom(
				this.businessRulesService.activatePolicyQuote({ ...session, pets })
			)
			setState(res)
			if (res.pets.length) {
				await firstValueFrom(dispatch(new PetAction.Load(res.pets)))
			}

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.RetrieveSessionBySearchResult)
	async retrieveSessionBySearchResult(
		{ dispatch }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.RetrieveSessionBySearchResult
	) {
		try {
			const result = await firstValueFrom(
				this.quoteSessionService.retrieveSessionBySearchResult(payload)
			)

			await firstValueFrom(dispatch(new QuoteSessionAction.LoadExistingQuote(result)))
			await firstValueFrom(dispatch(new QuoteFlowAction.DisableCheckForExistingQuotes()))

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.CloseQuote)
	async closeQuote({ dispatch }: StateContext<QuoteSessionStateModel>) {
		try {
			// await firstValueFrom(dispatch(new QuoteSessionAction.SaveSession()))
			await firstValueFrom(dispatch(new QuoteSessionAction.SaveForClose()))
			await firstValueFrom(dispatch(new QuoteSessionAction.CreateNew()))
			await firstValueFrom(
				dispatch(new ResetForm({ path: 'acknowledgement.acknowledgementsForm' }))
			)

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.EmailQuote)
	async emailQuote({ getState }: StateContext<QuoteSessionStateModel>) {
		try {
			const petList = (await firstValueFrom(this.store.select<PetStateModel>(PetState))).pets
			const pets = petList.map((p) => ({
				...p,
				species: p.species === Species.Dog ? 'Dog' : 'Cat',
			}))
			const session = getState()
			// @ts-expect-error issue with species needs to be fixed
			const res = await firstValueFrom(this.quoteSessionService.emailQuote({ ...session, pets }))
			if (!!res.sendEmailQuoteResponse) return Promise.reject(res.sendEmailQuoteResponse)

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.PurchasePolicy)
	async purchasePolicy(
		{ getState, setState }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.PurchasePolicy
	) {
		try {
			const state = getState()
			const requestWithPaymentInformation: QuoteSession = {
				...state,
				contact: {
					...state.contact,
					billingInfo: {
						...state.contact?.billingInfo,
						...payload.billing,
					},
					claimsPayment: {
						...state.contact!.claimsPayment!,
						...payload.claimsPayment,
					},
				},
			}
			const res: PurchaseResponse = await firstValueFrom(
				this.quoteSessionService.purchasePolicy(requestWithPaymentInformation)
			)
			if (!res) return Promise.reject('Something went wrong!')
			setState({
				...state,
				...res.session,
				locked: true,
			})

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Checkout)
	async checkout({ getState, setState }: StateContext<QuoteSessionStateModel>) {
		try {
			const state = getState()
			const checkoutRequest: QuoteSession = {
				...state,
			}
			const res = await firstValueFrom(
				this.quoteSessionService.checkout(state.quote?.id ?? '', checkoutRequest)
			)

			if (!res) return Promise.reject('Something went wrong!')
			setState({
				...state,
				paymentSessions: res.paymentSessions,
			})

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Purchase)
	async purchase(
		{ getState, setState }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Purchase
	) {
		try {
			const state = getState()
			const paymentMethodId = await this.stripeService.confirmSetup({
				email: payload?.contact?.email || '',
				name: `${payload.contact.firstName} ${payload.contact.lastName}`,
			})
			const purchaseRequest = {
				...state,
				paymentMethodId,
				contact: {
					...state.contact,
					claimsPayment: {
						...state.contact!.claimsPayment!,
						...payload.claimsPayment,
					},
				},
			}
			const res = await firstValueFrom(this.quoteSessionService.purchase(purchaseRequest))
			if (!res) return Promise.reject('Something went wrong!')

			this.stripeService.resetPaymentMethodId()
			setState({
				...state,
				contact: {
					...state.contact,
					policyNumber: res?.contact?.policyNumber,
				},
				locked: true,
			})

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Contact.Update)
	updateSessionContact(
		{ getState, setState }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Contact.Update
	) {
		const state = getState()
		const defaultAddress: Address = {
			line1: '',
			line2: '',
			city: '',
			state: '',
			isBilling: false,
			zipCode: '',
		}
		const defaultBillingInfo: BillingInformation = {
			paymentMethod: PaymentType.CreditCard,
			bankAccountNumber: '',
			bankAccountType: '',
			bankName: '',
			bankRoutingNumber: '',
			creditCardExpirationMonth: 1,
			creditCardExpirationYear: 22,
			creditCardNumber: '',
			creditCardSecurityCode: '',
			creditCardType: '',
		}

		setState({
			...state,
			contact: {
				...state.contact,
				...payload,
				mailingAddress: {
					...(state.contact?.mailingAddress ?? defaultAddress),
					...payload.mailingAddress,
				},
				billingAddress: {
					...(state.contact?.billingAddress ?? defaultAddress),
					...payload.billingAddress,
				},
				billingInfo: {
					...(state.contact?.billingInfo ?? defaultBillingInfo),
					...payload.billingInfo,
				},
			},
		})
	}

	@Action(QuoteSessionAction.Contact.GetStateGivenZip)
	getStateGivenZip(
		{ getState, setState }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Contact.GetStateGivenZip
	) {
		const state = getState()

		return this.quoteService.getStateGivenZip(state.contact?.ratingZipCode ?? payload).pipe(
			tap((ratingState) => {
				if (!ratingState) return
				if ('string' === typeof ratingState) {
					setState(
						patch<QuoteSessionStateModel>({
							contact: patch<Contact>({ ratingState }),
						})
					)
				}
			})
		)
	}

	@Action(QuoteSessionAction.Contact.ValidateAddress)
	async validateAddress(
		{ getState, setState }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Contact.ValidateAddress
	) {
		const state = getState()
		try {
			const contact = state.contact
			const address =
				payload === 'mailing' ? state.contact?.mailingAddress : state.contact?.billingAddress
			if (!contact || !address) return Promise.reject('No Contact Data Found')
			const validAddress = await firstValueFrom(this.businessRulesService.validateAddress(address))
			setState({
				...state,
				contact: {
					...state.contact,
					[payload === 'mailing' ? 'invalidMailingAddress' : 'invalidBillingAddress']:
						!validAddress,
				},
			})

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Contact.ValidateEmail)
	async validateEmail({ getState }: StateContext<QuoteSessionStateModel>) {
		const state = getState()
		try {
			const email = state.contact?.email ?? null

			if (!email) return false

			const isValidEmail = await firstValueFrom(this.businessRulesService.validateEmail(email))
			return isValidEmail
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Quote.Update)
	updateQuote(
		{ setState }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Quote.Update
	) {
		setState(
			patch<QuoteSessionStateModel>({
				quote: patch<Quote>(payload),
			})
		)
	}

	@Action(QuoteSessionAction.Quote.UpdateReferredAccount)
	async updateReferredAccount(
		{ setState, dispatch }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Quote.UpdateReferredAccount
	) {
		if (!payload) return
		try {
			await firstValueFrom(
				dispatch(new QuoteSessionAction.Quote.Update({ orgCode: payload.orgCode }))
			)
			setState(
				patch<QuoteSessionStateModel>({
					quote: patch<Quote>({ referredAccount: payload, referredSubAccount: null }),
				})
			)
			await firstValueFrom(dispatch(new QuoteSessionAction.SaveSession()))

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Quote.ClearReferredAccount)
	async clearReferredAccount({ getState, dispatch }: StateContext<QuoteSessionStateModel>) {
		const state = getState()
		const petState = this.store.selectSnapshot<Pet[]>((state) => state.pet.pets)
		try {
			const res = await firstValueFrom(
				this.quoteSessionService.clearReferredAccount({ ...state, pets: petState })
			)
			await firstValueFrom(dispatch(new QuoteSessionAction.Update(res)))
			await firstValueFrom(dispatch(new QuoteSessionAction.LoadTabProgress(res.tabProgress!)))
			if (!!res.pets.length) {
				await firstValueFrom(dispatch(new PetAction.Load(res.pets)))
			}
			await firstValueFrom(dispatch(new QuoteSessionAction.SaveSession()))

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Quote.UpdateReferredSubAccount)
	async updateReferredSubAccount(
		{ setState, dispatch }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.Quote.UpdateReferredSubAccount
	) {
		if (!payload) return
		try {
			await firstValueFrom(
				dispatch(new QuoteSessionAction.Contact.Update({ referringVetClinicId: payload.accountId }))
			)
			setState(
				patch<QuoteSessionStateModel>({
					quote: patch<Quote>({ referredSubAccount: payload }),
				})
			)

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Quote.ClearReferredSubAccount)
	async clearReferredSubAccount({
		getState,
		setState,
		dispatch,
	}: StateContext<QuoteSessionStateModel>) {
		const state = getState()
		try {
			await firstValueFrom(
				dispatch(
					new QuoteSessionAction.Contact.Update({
						referringVetClinicId: state.quote?.referredAccount?.accountId ?? null,
					})
				)
			)
			setState(
				patch<QuoteSessionStateModel>({
					quote: patch<Quote>({ referredSubAccount: null }),
				})
			)

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(QuoteSessionAction.Quote.ResetStartDate)
	resetStartDate({ setState }: StateContext<QuoteSessionStateModel>) {
		const policyStartDate: Date = new Date()
		const policyRenewalDate: Date = new Date()
		policyStartDate.setDate(policyStartDate.getDate() + 1)
		policyRenewalDate.setDate(policyStartDate.getDate() + 365)

		setState(
			patch<QuoteSessionStateModel>({
				quote: patch<Quote>({
					policyStartDate,
					policyRenewalDate,
				}),
			})
		)
	}

	@Action(QuoteSessionAction.LoadExistingQuote)
	async loadExistingQuote(
		{ dispatch }: StateContext<QuoteSessionStateModel>,
		{ payload }: QuoteSessionAction.LoadExistingQuote
	) {
		const policyStartDate = new Date()
		const policyRenewalDate = new Date()
		policyStartDate.setDate(policyStartDate.getDate() + 1)
		policyRenewalDate.setDate(policyStartDate.getDate() + 365)
		const quoteDefault: Quote = {
			policyStartDate,
			policyRenewalDate,
			paymentModality: 'Monthly',
		}

		if (payload.quote?.referredAccount?.accountId) {
			await firstValueFrom(
				dispatch(new ReferralAction.SearchSubAccount(payload.quote.referredAccount.accountId))
			)
		}

		const crmUserId = await firstValueFrom(this.store.select(UserState.crmUserId))
		await firstValueFrom(
			dispatch(
				new QuoteSessionAction.Load({
					...payload,
					contact: {
						...payload.contact,
						brand: !payload.contact?.brand ? 'embrace' : payload.contact.brand,
					},
					quote: !payload.quote ? quoteDefault : payload.quote,
					quoteStarted: true,
					crmUserId,
				})
			)
		)

		await firstValueFrom(dispatch(new QuoteSessionAction.Quote.ResetStartDate()))
		if (!!payload.pets) await firstValueFrom(dispatch(new PetAction.Load(payload.pets)))
	}

	@Selector()
	static contactState(state: QuoteSessionStateModel) {
		return state.contact
	}

	@Selector()
	static quoteState(state: QuoteSessionStateModel) {
		return state.quote
	}

	@Selector()
	static referralDiscountAvailable(state: QuoteSessionStateModel) {
		return (
			state.quote?.referredAccount?.orgBenefitParticipant ||
			state.quote?.referredSubAccount?.orgBenefitParticipant ||
			false
		)
	}

	@Selector()
	static referralDiscountPercentage(state: QuoteSessionStateModel) {
		return (
			state.quote?.referredAccount?.orgBenefitDiscountPercentage ||
			state.quote?.referredSubAccount?.orgBenefitDiscountPercentage ||
			0
		)
	}

	@Selector()
	static hasWellness(state: QuoteSessionStateModel) {
		return (state.quote?.totalWellness ?? 0) > 0
	}

	@Selector()
	static hasDog(state: QuoteSessionStateModel) {
		return state.pets?.find((p) => p.species?.toLowerCase() === 'dog')
	}

	@Selector()
	static hasCat(state: QuoteSessionStateModel) {
		return state.pets?.find((p) => p.species?.toLowerCase() === 'cat')
	}

	@Selector()
	static isProductVersion6orGreater(state: QuoteSessionStateModel) {
		return parseInt(state.quote?.productVersion ?? '0') >= 6
	}

	static isWhiteLabelCommunications() {
		return createSelector(
			[QuoteSessionState.contactState, BrandState],
			(contactState: Nullable<Contact>, brandState: BrandStateModel) => {
				if (!contactState) {
					return false
				}

				const brand = this.findCurrentBrand(brandState, contactState)

				return brand?.whiteLabelConfig?.isWhiteLabelCommunications ?? false
			}
		)
	}

	static currentBrandFriendlyName() {
		return createSelector(
			[QuoteSessionState.contactState, BrandState],
			(contactState: Nullable<Contact>, brandState: BrandStateModel) => {
				if (!contactState) {
					return ''
				}

				const brand = this.findCurrentBrand(brandState, contactState)

				return brand?.friendlyName ?? ''
			}
		)
	}

	static currentBrandName() {
		return createSelector(
			[QuoteSessionState.contactState, BrandState],
			(contactState: Nullable<Contact>, brandState: BrandStateModel) => {
				if (!contactState) {
					return ''
				}

				const brand = this.findCurrentBrand(brandState, contactState)

				return brand?.name ?? ''
			}
		)
	}

	private static findCurrentBrand(brandState: BrandStateModel, contactState: Nullable<Contact>) {
		if (!contactState) {
			return
		}

		return brandState.brands.find(
			(brand) => brand.name.toLowerCase() === contactState.brand?.toLowerCase()
		)
	}
}
